import React, {useEffect, useReducer, useState} from 'react';
import {LockClosedIcon} from '@heroicons/react/24/outline';
import {getErrorMessage} from 'utils';
import {xrStore} from 'lib';
import {Permissions} from 'types';
import {Button, Modal} from 'components';

const PERMISSIONS_DENIED = {
  ar: {
    title: 'Your AR is Turned Off',
    type: 'Augmented Reality',
    shortType: 'AR',
  },
  camera: {
    title: 'Your Camera is Turned Off',
    type: 'Camera',
    shortType: 'Camera',
  },
};

interface IARButtonEnterProps {
  style: React.CSSProperties;
}

const ARButtonEnter = ({style}: IARButtonEnterProps) => {
  const [permissionAR, setPermissionAR] = useState<Permissions>('prompt');
  const [permissionCam, setPermissionCam] = useState<Permissions>('prompt');
  const [showModal, toggleModal] = useReducer(s => !s, false);

  const handleClick = async () => {
    if (denied) {
      return toggleModal();
    }
    let session: XRSession | undefined;
    try {
      session = await xrStore.enterAR();
    } catch (error: any) {
      console.error(getErrorMessage(error));
    }
    if (session) {
      setPermissionAR('granted');
    } else {
      setPermissionAR('denied');
      toggleModal();
    }
  };

  useEffect(() => {
    navigator.permissions.query({name: 'camera'}).then(result => {
      setPermissionCam(result.state);
    });
  }, []);

  const deniedAR = permissionAR === 'denied';
  const deniedCam = permissionCam === 'denied';
  const denied = deniedAR || deniedCam;

  const deniedType = deniedAR ? 'ar' : deniedCam ? 'camera' : null;
  const modalTitle = deniedType ? PERMISSIONS_DENIED[deniedType].title : '';
  const permission = deniedType ? PERMISSIONS_DENIED[deniedType].type : '';

  const errorText = deniedType ? `${PERMISSIONS_DENIED[deniedType].shortType} is Turned Off` : '';

  return (
    <>
      <button type="button" style={style} className="cursor-pointer" onClick={handleClick}>
        Start AR
      </button>
      <p className="text-lg text-red-500 h-7">{errorText}</p>
      <Modal isVisible={showModal} usePortal={false}>
        <h2 className="text-2xl text-red-500 font-medium pb-4">{modalTitle}</h2>
        <p className="text-lg font-medium pb-1.5">Follow these steps to enable it:</p>
        <ul className="list-decimal text-slate-600 flex flex-col gap-1 px-5 pb-5">
          <li>
            Tap the{' '}
            <span>
              <LockClosedIcon className="size-5 inline pb-0.5" />
            </span>{' '}
            lock icon near the URL bar.
          </li>
          <li>Tap Permissions.</li>
          <li>Toggle {permission} to Allowed.</li>
          <li>Refresh the page.</li>
        </ul>
        <Button text="Refresh Page" onClick={() => location.reload()} />
      </Modal>
    </>
  );
};

export default React.memo(ARButtonEnter);
